// TODO: deprecated — superseded by IdeaTimeline. Only consumed by TimelineSection
// (rendered inside a hidden <div> in EditorApp) and TimelineConnector (also dead).
// Safe to delete once IdeaTimeline covers all flows (animation modals, layer timing, etc.).
import React, { Component } from "react";
import PropTypes from "prop-types";
import interact from "interactjs";
import TextAnimationModal from "../canvas/editor_shortcuts/TextAnimationModal";
import BoxAnimationModal from "../canvas/editor_shortcuts/BoxAnimationModal";
import {
  createAutomaticKeyframes,
  shouldCreateAutomaticKeyframesForLayerResize,
  shouldCreateAutomaticKeyframesForLayerMove,
} from "../../utils/automaticKeyframes";
import { getPluginDisplayName as getLocalizedPluginName } from "./IdeaTimeline/utils/buildLayers";
import "./RemotionTimeline.scss";

const svgPaths = {
  p3d5bee80: "M15 12L10.5 7.5L6 12",
  pf7e8100: "M1.33 6L8 2.67L14.67 6",
  p28db2b80: "M1.33 10L8 13.33L14.67 10",
  p3b86be00: "M2 3H14V11H2V3Z",
  p10fc0e80: "M2 2H14V14H2V2Z",
  p1691cc0: "M6 4L9 6L6 8L3 6Z",
  p16736e80: "M8 2C11.31 2 14 4.69 14 8S11.31 14 8 14S2 11.31 2 8S4.69 2 8 2Z",
  pb85f580: "M1 8S3 3 8 3S15 8 15 8S13 13 8 13S1 8 1 8Z",
  p36446d40: "M8 6C6.9 6 6 6.9 6 8S6.9 10 8 10S10 9.1 10 8S9.1 6 8 6Z",
  p341e6280:
    "M4 7V5C4 3.9 4.9 3 6 3H10C11.1 3 12 3.9 12 5V7H13C13.55 7 14 7.45 14 8V12C14 12.55 13.55 13 13 13H3C2.45 13 2 12.55 2 12V8C2 7.45 2.45 7 3 7H4Z",
};

class RemotionTimeline extends Component {
  constructor(props) {
    super(props);

    this.state = {
      playing: false,
      currentFrame: 0,
      duration: 1800, // 60 seconds at 30fps
      fps: 30,
      selectedLayers: [],
      layers: [],
      draggedLayer: null,
      showLayerPanel: true,
      timelineWidth: 1000,
      zoom: 3,
      contextMenu: null,
      selectedProperty: "position",
      animationSelectionDialog: null,
      showTextAnimationModal: false,
      showBoxAnimationModal: false,
      currentEditingLayer: null,
    };

    this.playerRef = React.createRef();
    this.timelineRef = React.createRef();
    this.layerPanelRef = React.createRef();
    this.currentPlayPromise = null;

    this.isDraggingPlayhead = false;

    this.handlePlay = this.handlePlay.bind(this);
    this.handlePause = this.handlePause.bind(this);
    this.handleSeek = this.handleSeek.bind(this);
    this.handleLayerSelect = this.handleLayerSelect.bind(this);
    this.handleLayerReorder = this.handleLayerReorder.bind(this);
    this.enableTimelineInteraction = this.enableTimelineInteraction.bind(this);
    this.updateLayerTiming = this.updateLayerTiming.bind(this);
    this.addNewLayer = this.addNewLayer.bind(this);
    this.deleteLayer = this.deleteLayer.bind(this);
    this.duplicateLayer = this.duplicateLayer.bind(this);
    this.showContextMenu = this.showContextMenu.bind(this);
    this.hideContextMenu = this.hideContextMenu.bind(this);
    this.addAnimation = this.addAnimation.bind(this);
    this.clearAllAnimations = this.clearAllAnimations.bind(this);
    this.startTextAnimationDrag = this.startTextAnimationDrag.bind(this);
    this.startBoxAnimationDrag = this.startBoxAnimationDrag.bind(this);
    this.startPlayheadDrag = this.startPlayheadDrag.bind(this);
    this.showAnimationSelectionDialog = this.showAnimationSelectionDialog.bind(this);
    this.hideAnimationSelectionDialog = this.hideAnimationSelectionDialog.bind(this);
    this.handleLayerClick = this.handleLayerClick.bind(this);
    this.openTextAnimationModal = this.openTextAnimationModal.bind(this);
    this.closeTextAnimationModal = this.closeTextAnimationModal.bind(this);
    this.openBoxAnimationModal = this.openBoxAnimationModal.bind(this);
    this.closeBoxAnimationModal = this.closeBoxAnimationModal.bind(this);
    this.onApplyTextAnimation = this.onApplyTextAnimation.bind(this);
    this.onApplyBoxAnimation = this.onApplyBoxAnimation.bind(this);
    this.onDeleteTextAnimation = this.onDeleteTextAnimation.bind(this);
    this.onDeleteBoxAnimation = this.onDeleteBoxAnimation.bind(this);
  }

  componentDidMount() {
    this.initializeLayers();
    this.enableTimelineInteraction();

    this.animationInterval = null;
    document.addEventListener("click", this.hideContextMenu);
  }

  componentWillUnmount() {
    if (this.animationInterval) {
      clearInterval(this.animationInterval);
    }
    document.removeEventListener("click", this.hideContextMenu);
  }

  componentDidUpdate(prevProps) {
    // Skip reinitialization if we're finalizing a drag operation or creating automatic keyframes
    if (this.isFinalizingDrag || this.isCreatingAutomaticKeyframes) {
      return;
    }

    const boxesChanged = prevProps.boxes !== this.props.boxes;
    const animationsChanged = prevProps.animations !== this.props.animations;
    const sceneChanged = prevProps.currentScene !== this.props.currentScene;
    const pluginToolbarsChanged = prevProps.pluginToolbars !== this.props.pluginToolbars;
    const soundListChanged = prevProps.soundList !== this.props.soundList;

    if (animationsChanged || sceneChanged || pluginToolbarsChanged || soundListChanged) {
      this.initializeLayers();
      return;
    }

    if (boxesChanged) {
      const prevBoxIds = Object.keys(prevProps.boxes || {});
      const currentBoxIds = Object.keys(this.props.boxes || {});

      const boxesAddedOrRemoved =
        prevBoxIds.length !== currentBoxIds.length ||
        !prevBoxIds.every((id) => currentBoxIds.includes(id));

      if (boxesAddedOrRemoved) {
        this.initializeLayers();
        return;
      }

      let textBoxAnimationsChanged = false;

      currentBoxIds.forEach((boxId) => {
        const prevBox = prevProps.boxes[boxId];
        const currentBox = this.props.boxes[boxId];

        if (prevBox && currentBox) {
          const prevTextAnimation = prevBox.textAnimation;
          const currentTextAnimation = currentBox.textAnimation;
          const prevBoxAnimation = prevBox.boxAnimation;
          const currentBoxAnimation = currentBox.boxAnimation;

          if (
            prevTextAnimation !== currentTextAnimation ||
            prevBoxAnimation !== currentBoxAnimation
          ) {
            textBoxAnimationsChanged = true;
          }
        }
      });

      if (textBoxAnimationsChanged) {
        this.initializeLayers();
      }
    }
  }

  startPlayheadDrag(event) {
    event.preventDefault();
    event.stopPropagation();

    const playheadEl = event.currentTarget;
    const rulerElement = playheadEl.closest(".timeline-ruler");
    if (!rulerElement) return;

    this.isDraggingPlayhead = true;
    playheadEl.classList.add("dragging");

    const updateFromClientX = (clientX) => {
      const rulerRect = rulerElement.getBoundingClientRect();
      const x = Math.max(0, Math.min(clientX - rulerRect.left, rulerRect.width));
      const frame = Math.floor((x / rulerRect.width) * this.state.duration);
      const newFrame = Math.max(0, Math.min(frame, this.state.duration));
      this.setState({ currentFrame: newFrame });
      this.handleSeek(newFrame);
    };

    const handleMouseMove = (e) => {
      e.preventDefault();
      updateFromClientX(e.clientX);
    };

    const handleMouseUp = (e) => {
      e.preventDefault();
      this.isDraggingPlayhead = false;
      playheadEl.classList.remove("dragging");
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  }

  initializeLayers() {
    const boxes = this.props.boxes || {};
    const animations = this.props.animations || {};
    const currentScene = this.props.currentScene;
    const navItemsById = this.props.navItemsById || {};
    const soundList = this.props.soundList || {};

    if (typeof boxes !== "object" || typeof animations !== "object") {
      this.setState({ layers: [] });
      return;
    }

    const boxIds = Object.keys(boxes).filter((id) => {
      const box = boxes[id];
      if (!box || typeof box !== "object") {
        return false;
      }

      if (currentScene) {
        const scene = navItemsById[currentScene];
        const belongsToScene =
          (scene && scene.boxes && scene.boxes.includes(id)) ||
          box.parent === currentScene ||
          box.container === currentScene;

        return belongsToScene;
      }

      return !id.includes("_");
    });

    const layers = boxIds.map((id, index) => {
      const box = boxes[id];
      const boxAnimations = Array.isArray(animations[id]) ? animations[id] : [];
      const hasAnimations = boxAnimations.length > 0;

      const textAnimation = box.textAnimation;
      const boxAnimation = box.boxAnimation;
      const hasTextAnimation = textAnimation && textAnimation.animation;
      const hasBoxAnimation = boxAnimation && boxAnimation.animation;

      const timeline = box.timeline || {};
      let startFrame =
        typeof timeline.startFrame === "number" ? Math.max(0, timeline.startFrame) : 0;
      let endFrame =
        typeof timeline.endFrame === "number"
          ? Math.max(startFrame + 30, timeline.endFrame)
          : this.state.duration / 3;

      if (hasAnimations && boxAnimations.length > 0) {
        const lastAnimation = boxAnimations[boxAnimations.length - 1];
        const lastAnimTime =
          typeof lastAnimation?.duration === "number" ? lastAnimation.duration : 0;
        if (lastAnimTime > 0) {
          endFrame = Math.max(endFrame, (lastAnimTime + 1) * (this.state.fps || 30));
        }
      }

      if (hasTextAnimation) {
        const textAnimEndTime = (textAnimation.delay || 0) + (textAnimation.duration || 2);
        endFrame = Math.max(endFrame, textAnimEndTime * (this.state.fps || 30));
      }

      if (hasBoxAnimation) {
        const boxAnimEndTime = (boxAnimation.delay || 0) + (boxAnimation.duration || 2);
        endFrame = Math.max(endFrame, boxAnimEndTime * (this.state.fps || 30));
      }

      return {
        id,
        name: this.getEnhancedLayerName(box, index),
        startFrame,
        endFrame,
        visible: box.visible !== false,
        locked: box.locked === true,
        zIndex: typeof box.layer === "number" ? box.layer : index,
        type: this.getLayerType(box),
        box: box,
        animations: boxAnimations,
        hasAnimations,
        canEdit: !box.locked,
        keyframes: [],
        textAnimation: hasTextAnimation ? textAnimation : null,
        boxAnimation: hasBoxAnimation ? boxAnimation : null,
        hasTextAnimation,
        hasBoxAnimation,
      };
    });

    const currentSceneSounds =
      currentScene && soundList[currentScene] ? soundList[currentScene] : [];
    const narrationAudio = currentSceneSounds.find((audio) => audio && audio.type === "narration");

    const sceneAudio =
      narrationAudio || (currentSceneSounds.length > 0 ? currentSceneSounds[0] : null);

    if (sceneAudio) {
      const fps = this.state.fps;
      const startTimeSeconds = parseFloat(sceneAudio.startTime || 0);
      const endTimeSeconds = parseFloat(sceneAudio.endTime || 30);

      const startFrame = Math.max(0, Math.floor(startTimeSeconds * fps));
      const endFrame = Math.max(startFrame + 30, Math.floor(endTimeSeconds * fps));

      const sceneAudioLayer = {
        id: "scene-audio",
        name: sceneAudio.name || sceneAudio.title || "Scene Audio",
        startFrame,
        endFrame,
        visible: true,
        locked: false,
        zIndex: -1,
        type: "scene-audio",
        box: { pluginId: "scene-audio" },
        animations: [],
        hasAnimations: false,
        canEdit: false,
        keyframes: [],
        textAnimation: null,
        boxAnimation: null,
        hasTextAnimation: false,
        hasBoxAnimation: false,
        audioData: sceneAudio,
      };

      layers.unshift(sceneAudioLayer);
    }
    layers.sort((a, b) => a.zIndex - b.zIndex);
    this.setState({ layers });
  }

  getLayerType(box) {
    if (!box.pluginId) return "unknown";

    const pluginId = box.pluginId.toLowerCase();
    if (pluginId === "scene-audio") return "scene-audio";
    if (pluginId.includes("image") || pluginId.includes("photo")) return "image";
    if (pluginId.includes("text") || pluginId.includes("title")) return "text";
    if (pluginId.includes("video") || pluginId.includes("player")) return "video";
    if (pluginId.includes("audio") || pluginId.includes("sound")) return "audio";
    if (pluginId.includes("shape")) return "shape";

    return "element";
  }

  getPluginDisplayName(box) {
    const pluginToolbars = this.props.pluginToolbars || {};
    const toolbar = pluginToolbars[box.id];
    const pluginId = toolbar ? toolbar.pluginId : box.pluginId;
    // Localized "<Id>.PluginName" via the shared helper (all languages).
    return getLocalizedPluginName(pluginId);
  }

  getContentPreview(box) {
    try {
      const pluginToolbars = this.props.pluginToolbars || {};
      const toolbar = pluginToolbars[box.id];
      const state = toolbar ? toolbar.state : null;
      const content = box.content || {};

      if (state && state.__text && typeof state.__text === "string") {
        let textContent = state.__text;
        try {
          textContent = decodeURIComponent(textContent);
        } catch (e) {}
        textContent = textContent.replace(/<[^>]*>/g, "").trim();
        if (textContent && textContent.length > 0) {
          return textContent.length > 30 ? textContent.substring(0, 30) + "..." : textContent;
        }
      }

      if (content.text) {
        const textContent =
          typeof content.text === "string" ? content.text.replace(/<[^>]*>/g, "").trim() : "";
        if (textContent && textContent.length > 0) {
          return textContent.length > 25 ? textContent.substring(0, 25) + "..." : textContent;
        }
      }

      if (state && state.url && typeof state.url === "string") {
        const filename = state.url.split("/").pop() || "file";
        const nameWithoutExt = filename.split(".")[0];
        return nameWithoutExt.length > 20
          ? nameWithoutExt.substring(0, 20) + "..."
          : nameWithoutExt;
      }

      if (content.title && typeof content.title === "string") {
        return content.title.length > 25 ? content.title.substring(0, 25) + "..." : content.title;
      }

      if (content.url && typeof content.url === "string") {
        const filename = content.url.split("/").pop() || "file";
        const nameWithoutExt = filename.split(".")[0];
        return nameWithoutExt.length > 20
          ? nameWithoutExt.substring(0, 20) + "..."
          : nameWithoutExt;
      }

      if (state && state.question && typeof state.question === "string") {
        const questionText = state.question.replace(/<[^>]*>/g, "").trim();
        return questionText.length > 25 ? questionText.substring(0, 25) + "..." : questionText;
      }

      if (content.question && typeof content.question === "string") {
        const questionText = content.question.replace(/<[^>]*>/g, "").trim();
        return questionText.length > 25 ? questionText.substring(0, 25) + "..." : questionText;
      }
    } catch (error) {
      console.warn("Error getting content preview:", error);
    }

    return null;
  }

  getEnhancedLayerName(box, index) {
    if (box.title && typeof box.title === "string" && box.title.trim()) {
      return box.title;
    }

    const pluginDisplayName = this.getPluginDisplayName(box);
    const contentPreview = this.getContentPreview(box);

    if (contentPreview) {
      return contentPreview;
    }

    const pluginToolbars = this.props.pluginToolbars || {};
    const currentToolbar = pluginToolbars[box.id];
    const currentPluginId = currentToolbar ? currentToolbar.pluginId : null;

    const existingLayersWithSameName = this.state.layers
      ? this.state.layers.filter((layer) => {
          const layerToolbar = pluginToolbars[layer.box.id];
          const layerPluginId = layerToolbar ? layerToolbar.pluginId : null;
          return layer.box && layerPluginId === currentPluginId && !layer.box.title;
        }).length
      : 0;

    const nameIndex = existingLayersWithSameName + 1;
    return nameIndex > 1 ? `${pluginDisplayName} ${nameIndex}` : pluginDisplayName;
  }

  getAnimationType(animation) {
    if (!animation) return "unknown";

    if (animation.name) {
      const name = animation.name.toLowerCase();
      if (name.includes("fade") || name.includes("opacity")) return "opacity";
      if (name.includes("zoom") || name.includes("scale")) return "scale";
      if (name.includes("rotate") || name.includes("spin") || name.includes("roll"))
        return "rotation";
      if (name.includes("move") || name.includes("slide")) return "position";
    }

    if (animation.x !== undefined || animation.y !== undefined) return "position";
    if (animation.opacity !== undefined) return "opacity";
    if (animation.width !== undefined || animation.height !== undefined) return "scale";
    if (animation.rotation !== undefined || animation.rotate !== undefined) return "rotation";
    if (animation.scaleX !== undefined || animation.scaleY !== undefined) return "scale";

    return "custom";
  }

  enableTimelineInteraction() {
    if (!this.timelineRef.current) return;

    interact(".time-ruler").draggable({
      listeners: {
        move: (event) => {
          const rulerElement = event.target.closest(".time-ruler");
          if (!rulerElement) return;

          const rulerRect = rulerElement.getBoundingClientRect();
          const x = Math.max(0, Math.min(event.clientX - rulerRect.left, rulerRect.width));
          const frame = Math.floor((x / rulerRect.width) * this.state.duration);
          const newFrame = Math.max(0, Math.min(frame, this.state.duration));

          this.setState({ currentFrame: newFrame });
          this.handleSeek(newFrame);
        },
      },
    });

    interact(".timeline-tracks").draggable({
      listeners: {
        move: (event) => {
          const tracksElement = event.target.closest(".timeline-tracks");
          if (!tracksElement || event.target.closest(".timeline-layer")) return;

          const tracksRect = tracksElement.getBoundingClientRect();
          const x = Math.max(0, Math.min(event.clientX - tracksRect.left, tracksRect.width));
          const frame = Math.floor((x / tracksRect.width) * this.state.duration);
          const newFrame = Math.max(0, Math.min(frame, this.state.duration));

          this.setState({ currentFrame: newFrame });
          this.handleSeek(newFrame);
        },
      },
    });

    interact(".timeline-layer.editable:not(.locked)")
      .draggable({
        listeners: {
          start: (event) => {
            if (this.isDraggingAnimation) return false;
            const layerId = event.target.dataset.layerId;
            const layer = this.state.layers.find((l) => l.id === layerId);

            console.log("🔍 DRAG START - Layer data:", {
              layerId,
              layer,
              isDraggingAnimation: this.isDraggingAnimation,
            });

            if (layer) {
              // Store original values on the instance for immediate access
              this.dragOriginalValues = {
                layerId: layerId,
                originalStartFrame: layer.startFrame,
                originalEndFrame: layer.endFrame,
                originalTextDelay: layer.textAnimation?.delay || 0,
                originalBoxDelay: layer.boxAnimation?.delay || 0,
              };

              console.log("🔍 DRAG START - Stored dragOriginalValues:", this.dragOriginalValues);

              this.setState({ draggedLayer: layerId });
            } else {
              this.setState({ draggedLayer: layerId });
            }

            event.target.style.zIndex = 1000;
            event.target.classList.add("dragging");
          },
          move: (event) => {
            if (this.isDraggingAnimation) return;
            const target = event.target;
            const tracksContainer = target.closest(".timeline-tracks");
            const tracksRect = tracksContainer.getBoundingClientRect();

            let x = event.clientX - tracksRect.left;
            const containerWidth = tracksRect.width;

            x = Math.max(0, Math.min(x, containerWidth));

            const newStartFrame = Math.floor((x / containerWidth) * this.state.duration);
            const layer = this.state.layers.find((l) => l.id === target.dataset.layerId);

            if (layer && layer.canEdit && !layer.locked) {
              const layerDuration = layer.endFrame - layer.startFrame;
              const newEndFrame = newStartFrame + layerDuration;

              if (newStartFrame >= 0 && newEndFrame <= this.state.duration) {
                const leftPercent = (newStartFrame / this.state.duration) * 100;
                target.style.left = `${Math.max(0, Math.min(leftPercent, 100))}%`;

                let updatedLayers = this.state.layers.map((l) => {
                  if (l.id === layer.id) {
                    const updatedLayer = {
                      ...l,
                      startFrame: newStartFrame,
                      endFrame: newEndFrame,
                    };

                    if (l.hasAnimations && l.animations) {
                      updatedLayer.keyframes = [];
                    }

                    return updatedLayer;
                  }
                  return l;
                });

                // Update animation delays during drag for real-time visual feedback
                if (this.dragOriginalValues && this.dragOriginalValues.layerId === layer.id) {
                  const originalStartFrame = this.dragOriginalValues.originalStartFrame;
                  const timeDelta = (newStartFrame - originalStartFrame) / this.state.fps;

                  updatedLayers = updatedLayers.map((l) => {
                    if (l.id === layer.id) {
                      const updatedLayer = { ...l };

                      if (l.textAnimation) {
                        const originalDelay = this.dragOriginalValues.originalTextDelay;
                        const newDelay = Math.max(0, originalDelay + timeDelta);
                        updatedLayer.textAnimation = {
                          ...l.textAnimation,
                          delay: newDelay,
                          _isDragging: true, // Flag to indicate we're dragging
                        };
                      }

                      if (l.boxAnimation) {
                        const originalDelay = this.dragOriginalValues.originalBoxDelay;
                        const newDelay = Math.max(0, originalDelay + timeDelta);
                        updatedLayer.boxAnimation = {
                          ...l.boxAnimation,
                          delay: newDelay,
                          _isDragging: true,
                        };
                      }

                      return updatedLayer;
                    }
                    return l;
                  });
                }

                this.setState({ layers: updatedLayers });
              }
            }
          },
          end: (event) => {
            const layerId = event.target.dataset.layerId;
            const layer = this.state.layers.find((l) => l.id === layerId);

            if (this.dragOriginalValues && this.dragOriginalValues.layerId === layerId) {
              const updatedLayers = this.state.layers.map((l) => {
                if (l.id === layerId) {
                  const updatedLayer = {
                    ...l,
                    // Preserve the current position from dragging
                    startFrame: l.startFrame,
                    endFrame: l.endFrame,
                  };

                  if (l.textAnimation) {
                    updatedLayer.textAnimation = {
                      ...l.textAnimation,
                      delay: l.textAnimation.delay, // Keep the current delay from dragging
                    };
                    delete updatedLayer.textAnimation._isDragging;
                  }

                  if (l.boxAnimation) {
                    updatedLayer.boxAnimation = {
                      ...l.boxAnimation,
                      delay: l.boxAnimation.delay, // Keep the current delay from dragging
                    };
                    delete updatedLayer.boxAnimation._isDragging;
                  }

                  return updatedLayer;
                }
                return l;
              });

              this.setState({ layers: updatedLayers }, () => {
                // Set flag to prevent initializeLayers during finalization
                this.isFinalizingDrag = true;
                this.finalizeAnimationDelays(layerId);

                // Check if we should create automatic keyframes for layer movement
                const oldLayer = this.dragOriginalValues
                  ? {
                      startFrame: this.dragOriginalValues.originalStartFrame,
                      endFrame:
                        this.dragOriginalValues.originalStartFrame +
                        (layer.endFrame - layer.startFrame),
                    }
                  : null;
                const newLayer = layer;
                const box = this.props.boxes?.[layerId];
                const existingAnimations = this.props.animations?.[layerId] || [];

                console.log("🔍 DRAG END - Checking automatic keyframes:", {
                  layerId,
                  oldLayer,
                  newLayer,
                  existingAnimations,
                  shouldCreate: shouldCreateAutomaticKeyframesForLayerMove(
                    oldLayer,
                    newLayer,
                    existingAnimations
                  ),
                });

                if (
                  shouldCreateAutomaticKeyframesForLayerMove(oldLayer, newLayer, existingAnimations)
                ) {
                  console.log("🔍 DRAG END - Creating automatic keyframes for layer movement");
                  const layerStartTime = newLayer.startFrame / this.state.fps;
                  const layerEndTime = newLayer.endFrame / this.state.fps;
                  const domElement = document.querySelector(`[data-box-id="${layerId}"]`);

                  console.log("🔍 DRAG END - Keyframe data:", {
                    layerStartTime,
                    layerEndTime,
                    box,
                    domElement,
                  });

                  this.isCreatingAutomaticKeyframes = true;
                  const keyframes = createAutomaticKeyframes(
                    layerStartTime,
                    layerEndTime,
                    box,
                    domElement
                  );
                  console.log("🔍 DRAG END - Generated keyframes:", keyframes);
                  this.props.onSaveNewAnimation(layerId, keyframes);

                  setTimeout(() => {
                    this.isCreatingAutomaticKeyframes = false;
                  }, 100);
                }

                // Clear flag after a brief delay to allow prop updates to complete
                setTimeout(() => {
                  this.isFinalizingDrag = false;
                }, 100);

                // Clean up stored drag values after automatic keyframe creation
                this.dragOriginalValues = null;
              });
            } else {
              // Clean up stored drag values if no layer update
              this.dragOriginalValues = null;
            }

            this.setState({ draggedLayer: null });
            event.target.style.zIndex = "";
            event.target.classList.remove("dragging");
            this.forceUpdate();
          },
        },
      })
      .resizable({
        edges: { left: true, right: true },
        listeners: {
          start: (event) => {
            if (event.target.classList.contains("dragging") || this.isDraggingAnimation)
              return false;

            const layerId = event.target.dataset.layerId;
            const layer = this.state.layers.find((l) => l.id === layerId);

            if (layer) {
              // Store original values on the instance for immediate access (same as drag)
              this.dragOriginalValues = {
                layerId: layerId,
                originalStartFrame: layer.startFrame,
                originalEndFrame: layer.endFrame,
                originalTextDelay: layer.textAnimation?.delay || 0,
                originalBoxDelay: layer.boxAnimation?.delay || 0,
              };
            }

            event.target.style.zIndex = 1000;
            event.target.classList.add("resizing");
          },
          move: (event) => {
            const target = event.target;
            if (target.classList.contains("dragging") || this.isDraggingAnimation) return false;

            const layerId = target.dataset.layerId;
            const layer = this.state.layers.find((l) => l.id === layerId);

            if (!layer || !layer.canEdit || layer.locked) return;

            const tracksContainer = target.closest(".timeline-tracks");
            const tracksRect = tracksContainer.getBoundingClientRect();
            const containerWidth = tracksRect.width;

            const leftPercent = Math.max(
              0,
              ((event.rect.left - tracksRect.left) / containerWidth) * 100
            );
            const rightPercent = Math.min(
              100,
              ((event.rect.right - tracksRect.left) / containerWidth) * 100
            );
            const widthPercent = rightPercent - leftPercent;

            const startFrame = Math.floor((leftPercent / 100) * this.state.duration);
            const endFrame = Math.floor((rightPercent / 100) * this.state.duration);

            const minDurationFrames = Math.max(10, this.state.fps * 0.1);
            if (
              startFrame >= 0 &&
              endFrame <= this.state.duration &&
              endFrame - startFrame >= minDurationFrames &&
              widthPercent >= 1
            ) {
              let updatedLayers = this.state.layers.map((l) => {
                if (l.id === layerId) {
                  const updatedLayer = {
                    ...l,
                    startFrame: startFrame,
                    endFrame: endFrame,
                  };

                  if (l.hasAnimations && l.animations) {
                    updatedLayer.keyframes = [];
                  }

                  return updatedLayer;
                }
                return l;
              });

              // Update animation delays during resize for real-time visual feedback
              if (this.dragOriginalValues && this.dragOriginalValues.layerId === layerId) {
                const originalStartFrame = this.dragOriginalValues.originalStartFrame;
                const timeDelta = (startFrame - originalStartFrame) / this.state.fps;

                updatedLayers = updatedLayers.map((l) => {
                  if (l.id === layerId) {
                    const updatedLayer = { ...l };

                    if (l.textAnimation) {
                      const originalDelay = this.dragOriginalValues.originalTextDelay;
                      const newDelay = Math.max(0, originalDelay + timeDelta);
                      updatedLayer.textAnimation = {
                        ...l.textAnimation,
                        delay: newDelay,
                        _isDragging: true,
                      };
                    }

                    if (l.boxAnimation) {
                      const originalDelay = this.dragOriginalValues.originalBoxDelay;
                      const newDelay = Math.max(0, originalDelay + timeDelta);
                      updatedLayer.boxAnimation = {
                        ...l.boxAnimation,
                        delay: newDelay,
                        _isDragging: true,
                      };
                    }

                    return updatedLayer;
                  }
                  return l;
                });
              }

              this.setState({ layers: updatedLayers });
            }
          },
          end: (event) => {
            const layerId = event.target.dataset.layerId;
            const layer = this.state.layers.find((l) => l.id === layerId);

            if (this.dragOriginalValues && this.dragOriginalValues.layerId === layerId) {
              const updatedLayers = this.state.layers.map((l) => {
                if (l.id === layerId) {
                  const updatedLayer = {
                    ...l,
                    // Preserve the current position from resizing
                    startFrame: l.startFrame,
                    endFrame: l.endFrame,
                  };

                  if (l.textAnimation) {
                    updatedLayer.textAnimation = {
                      ...l.textAnimation,
                      delay: l.textAnimation.delay, // Keep the current delay from resizing
                    };
                    delete updatedLayer.textAnimation._isDragging;
                  }

                  if (l.boxAnimation) {
                    updatedLayer.boxAnimation = {
                      ...l.boxAnimation,
                      delay: l.boxAnimation.delay, // Keep the current delay from resizing
                    };
                    delete updatedLayer.boxAnimation._isDragging;
                  }

                  return updatedLayer;
                }
                return l;
              });

              this.setState({ layers: updatedLayers }, () => {
                // Set flag to prevent initializeLayers during finalization
                this.isFinalizingDrag = true;
                this.finalizeAnimationDelays(layerId);
                // Clear flag after a brief delay to allow prop updates to complete
                setTimeout(() => {
                  this.isFinalizingDrag = false;
                }, 100);
              });
            }

            // Check if we should create automatic keyframes for layer resize - ASYNC to prevent layer reset
            if (this.dragOriginalValues && layer) {
              const originalLayer = {
                startFrame: this.dragOriginalValues.originalStartFrame,
                endFrame: this.dragOriginalValues.originalEndFrame,
              };

              if (
                shouldCreateAutomaticKeyframesForLayerResize(originalLayer, layer, layer.animations)
              ) {
                // Store values for async creation
                const asyncData = {
                  layerId: layer.boxId || layerId,
                  layerStartTime: layer.startFrame / (this.state.fps || 30),
                  layerEndTime: layer.endFrame / (this.state.fps || 30),
                };

                // Create keyframes AFTER layer state has fully settled
                setTimeout(() => {
                  // Set flag to prevent initializeLayers when we add automatic keyframes
                  this.isCreatingAutomaticKeyframes = true;

                  const currentBox = this.props.boxes ? this.props.boxes[asyncData.layerId] : null;
                  const domElement = document.getElementById(`box-${asyncData.layerId}`);
                  const automaticKeyframes = createAutomaticKeyframes(
                    asyncData.layerStartTime,
                    asyncData.layerEndTime,
                    currentBox,
                    domElement
                  );

                  if (this.props.onSaveNewAnimation) {
                    this.props.onSaveNewAnimation(asyncData.layerId, automaticKeyframes);
                  }

                  // Clear flag after a delay to allow animation props to update
                  setTimeout(() => {
                    this.isCreatingAutomaticKeyframes = false;
                  }, 200);
                }, 150); // Wait for all layer updates to complete
              }
            }

            // Clean up stored drag values AFTER using them
            this.dragOriginalValues = null;

            event.target.style.zIndex = "";
            event.target.classList.remove("resizing");
            this.forceUpdate();
          },
        },
      });
  }

  handlePlay() {
    this.setState({ playing: true, currentFrame: 0 });
    if (this.playerRef.current) {
      this.currentPlayPromise = this.playerRef.current.play();
      if (this.currentPlayPromise !== undefined) {
        this.currentPlayPromise
          .then(() => {
            this.currentPlayPromise = null;
          })
          .catch((error) => {
            console.warn("Play request was interrupted:", error);
            this.setState({ playing: false });
            this.currentPlayPromise = null;
          });
      }
    }

    if (this.props.onAnimationReset) {
      this.props.onAnimationReset();
    }

    this.startAnimationPreview();

    if (this.props.playAnimation) {
      this.props.playAnimation();
    }

    if (this.props.onPlay) {
      this.props.onPlay();
    }
  }

  handlePause() {
    this.setState({ playing: false });

    if (this.playerRef.current) {
      if (this.currentPlayPromise) {
        this.currentPlayPromise
          .then(() => {
            this.playerRef.current.pause();
          })
          .catch(() => {
            // Play was already interrupted, no need to pause
          });
      } else {
        this.playerRef.current.pause();
      }
    }

    this.pauseAnimationPreview();

    if (this.props.pauseAnimation) {
      this.props.pauseAnimation();
    }

    if (this.props.onPause) {
      this.props.onPause();
    }
  }

  startAnimationPreview() {
    const { currentFrame, fps } = this.state;
    const currentTime = currentFrame / fps;

    this.triggerAnimationsAtTime(currentTime);

    this.animationInterval = setInterval(() => {
      const newFrame = this.state.currentFrame + 1;
      if (newFrame <= this.state.duration) {
        this.setState({ currentFrame: newFrame });
        const newTime = newFrame / fps;
        this.triggerAnimationsAtTime(newTime);
      } else {
        this.handlePause();
        this.setState({ currentFrame: 0 });
      }
    }, 1000 / fps);
  }

  pauseAnimationPreview() {
    if (this.animationInterval) {
      clearInterval(this.animationInterval);
      this.animationInterval = null;
    }

    if (this.props.onPauseAnimations) {
      this.props.onPauseAnimations();
    }
  }

  triggerLayerAnimation(layerId, relativeTime) {
    if (this.props.onTriggerAnimation) {
      this.props.onTriggerAnimation(layerId, relativeTime);
    }
  }

  handleSeek(frame) {
    this.setState({ currentFrame: frame });
    if (this.playerRef.current) {
      this.playerRef.current.seekTo(frame);
    }

    this.updateAnimationPositions(frame);

    const currentTime = frame / this.state.fps;
    if (this.props.setCurrentTime) {
      // The playhead was moved on purpose: the narration follows it.
      this.props.setCurrentTime(currentTime, { seek: true });
    }

    if (this.props.onSeek) {
      this.props.onSeek(frame);
    }
  }

  updateAnimationPositions(frame) {
    const { fps } = this.state;
    const currentTime = frame / fps;

    this.triggerAnimationsAtTime(currentTime);
  }

  triggerAnimationsAtTime(currentTime) {
    const { layers } = this.state;

    layers.forEach((layer) => {
      if (!layer.visible) {
        return;
      }

      const layerStartTime = (layer.startFrame || 0) / (this.state.fps || 30);
      const layerEndTime = (layer.endFrame || this.state.duration) / (this.state.fps || 30);

      if (currentTime < layerStartTime || currentTime > layerEndTime) {
        return;
      }

      const relativeTime = currentTime - layerStartTime;

      // Handle keyframe animations if they exist
      if (layer.hasAnimations && layer.animations && Array.isArray(layer.animations)) {
        if (layer.animations.length >= 2) {
          // Find the appropriate keyframe pair for the current time
          for (let i = 0; i < layer.animations.length - 1; i++) {
            const currentKeyframe = layer.animations[i];
            const nextKeyframe = layer.animations[i + 1];

            if (!currentKeyframe || !nextKeyframe) continue;

            const startTime =
              typeof currentKeyframe.startTime === "number"
                ? currentKeyframe.startTime
                : typeof currentKeyframe.duration === "number"
                  ? currentKeyframe.duration
                  : i;
            const endTime =
              typeof nextKeyframe.startTime === "number"
                ? nextKeyframe.startTime
                : typeof nextKeyframe.duration === "number"
                  ? nextKeyframe.duration
                  : i + 1;

            if (relativeTime >= startTime && relativeTime <= endTime) {
              const segmentDuration = endTime - startTime;
              const progress =
                segmentDuration > 0 ? (relativeTime - startTime) / segmentDuration : 0;
              const clampedProgress = Math.min(1, Math.max(0, progress));

              if (this.props.onTriggerLayerAnimation) {
                this.props.onTriggerLayerAnimation(layer.id, currentTime, {
                  animation: currentKeyframe,
                  progress: clampedProgress,
                  duration: segmentDuration,
                  isActive: true,
                  animationIndex: i,
                  layerRelativeTime: relativeTime,
                  startKeyframe: currentKeyframe,
                  endKeyframe: nextKeyframe,
                });
              }
              break;
            }
          }
        } else if (layer.animations.length === 1) {
          // Single keyframe - just apply it
          const keyframe = layer.animations[0];
          const startTime =
            typeof keyframe.startTime === "number"
              ? keyframe.startTime
              : typeof keyframe.duration === "number"
                ? keyframe.duration
                : 0;

          if (relativeTime >= startTime) {
            if (this.props.onTriggerLayerAnimation) {
              this.props.onTriggerLayerAnimation(layer.id, currentTime, {
                animation: keyframe,
                progress: 1,
                duration: 0,
                isActive: true,
                animationIndex: 0,
                layerRelativeTime: relativeTime,
                startKeyframe: keyframe,
                endKeyframe: keyframe,
              });
            }
          }
        }
      }

      // Always trigger for text and box animations (they handle their own timing)
      if (this.props.onTriggerLayerAnimation) {
        this.props.onTriggerLayerAnimation(layer.id, currentTime, {
          isTimeUpdate: true,
          layerRelativeTime: relativeTime,
        });
      }
    });

    if (this.props.onTimeUpdate) {
      this.props.onTimeUpdate(currentTime, Math.floor(currentTime * (this.state.fps || 30)));
    }
  }

  handleLayerSelect(layerId) {
    const { selectedLayers } = this.state;
    const isSelected = selectedLayers.includes(layerId);

    let newSelection;
    if (isSelected) {
      newSelection = selectedLayers.filter((id) => id !== layerId);
    } else {
      newSelection = [...selectedLayers, layerId];
    }

    this.setState({ selectedLayers: newSelection });

    if (this.props.onLayerSelect) {
      this.props.onLayerSelect(layerId, !isSelected);
    }
  }

  handleLayerReorder(layerId, direction) {
    const { layers } = this.state;

    if (!Array.isArray(layers) || !layerId) return;

    const layerIndex = layers.findIndex((l) => l && l.id === layerId);

    if (layerIndex === -1) return;

    let newIndex;
    if (direction === "up") {
      newIndex = Math.min(layerIndex + 1, layers.length - 1);
    } else if (direction === "down") {
      newIndex = Math.max(layerIndex - 1, 0);
    } else if (direction === "front") {
      newIndex = layers.length - 1;
    } else if (direction === "back") {
      newIndex = 0;
    } else if (typeof direction === "number") {
      newIndex = Math.max(0, Math.min(direction, layers.length - 1));
    } else {
      return;
    }

    if (newIndex === layerIndex) return;

    const newLayers = [...layers];
    const [movedLayer] = newLayers.splice(layerIndex, 1);

    if (!movedLayer) return;

    newLayers.splice(newIndex, 0, movedLayer);

    newLayers.forEach((layer, index) => {
      if (layer) {
        layer.zIndex = index;
      }
    });

    this.setState({ layers: newLayers });

    if (this.props.onLayerReorder) {
      this.props.onLayerReorder(layerId, newIndex, movedLayer.zIndex);
    }
  }

  addNewLayer() {
    const { layers, duration } = this.state;
    const newLayer = {
      id: `layer_${Date.now()}`,
      name: `New Layer ${layers.length + 1}`,
      startFrame: 0,
      endFrame: duration / 3,
      visible: true,
      locked: false,
      zIndex: layers.length,
      type: "element",
      box: null,
    };

    this.setState({
      layers: [...layers, newLayer],
      selectedLayers: [newLayer.id],
    });

    if (this.props.onAddLayer) {
      this.props.onAddLayer(newLayer);
    }
  }

  deleteLayer(layerId) {
    const { layers, selectedLayers } = this.state;
    const newLayers = layers.filter((l) => l.id !== layerId);
    const newSelected = selectedLayers.filter((id) => id !== layerId);

    this.setState({
      layers: newLayers,
      selectedLayers: newSelected,
    });

    if (this.props.onDeleteLayer) {
      this.props.onDeleteLayer(layerId);
    }
  }

  duplicateLayer(layerId) {
    const { layers } = this.state;
    const originalLayer = layers.find((l) => l.id === layerId);

    if (!originalLayer) return;

    const duplicatedLayer = {
      ...originalLayer,
      id: `${originalLayer.id}_copy_${Date.now()}`,
      name: `${originalLayer.name} Copy`,
      zIndex: layers.length,
    };

    this.setState({
      layers: [...layers, duplicatedLayer],
      selectedLayers: [duplicatedLayer.id],
    });

    if (this.props.onDuplicateLayer) {
      this.props.onDuplicateLayer(duplicatedLayer);
    }
  }

  toggleLayerVisibility(layerId) {
    const { layers } = this.state;
    const layer = layers.find((l) => l.id === layerId);
    const newVisibility = !layer.visible;

    const newLayers = layers.map((l) => {
      if (l.id === layerId) {
        return { ...l, visible: newVisibility };
      }
      return l;
    });

    this.setState({ layers: newLayers });

    if (this.props.onLayerVisibilityToggle) {
      this.props.onLayerVisibilityToggle(layerId, newVisibility);
    }

    if (this.props.onUpdateBoxVisibility) {
      this.props.onUpdateBoxVisibility(layerId, newVisibility);
    }
  }

  toggleLayerLock(layerId) {
    const { layers } = this.state;
    const newLayers = layers.map((layer) => {
      if (layer.id === layerId) {
        return { ...layer, locked: !layer.locked };
      }
      return layer;
    });

    this.setState({ layers: newLayers });
  }

  finalizeAnimationDelays(layerId) {
    const { layers } = this.state;
    const layer = layers.find((l) => l.id === layerId);

    if (!layer) return;

    if (layer.textAnimation && this.props.onTextAnimationUpdate) {
      this.props.onTextAnimationUpdate(layerId, { delay: layer.textAnimation.delay });
    }

    if (layer.boxAnimation && this.props.onBoxAnimationUpdate) {
      this.props.onBoxAnimationUpdate(layerId, { delay: layer.boxAnimation.delay });
    }

    if (this.props.onLayerUpdate) {
      this.props.onLayerUpdate(layer);
    }
  }

  updateLayerTiming(layerId, startFrame, endFrame, isDragging = false) {
    const { layers } = this.state;
    const layer = layers.find((l) => l.id === layerId);

    if (!layer) return;

    const clampedStartFrame = Math.max(0, Math.floor(startFrame));
    const clampedEndFrame = Math.min(this.state.duration, Math.floor(endFrame));

    if (clampedEndFrame <= clampedStartFrame) return;

    const timeDelta = (clampedStartFrame - layer.startFrame) / this.state.fps;

    const newLayers = layers.map((l) => {
      if (l.id === layerId) {
        const updatedLayer = {
          ...l,
          startFrame: clampedStartFrame,
          endFrame: clampedEndFrame,
        };

        if (l.hasAnimations && l.animations) {
          updatedLayer.keyframes = [];
        }

        if (!isDragging && l.textAnimation && timeDelta !== 0) {
          const currentDelay = l.textAnimation.delay || 0;
          const newDelay = Math.max(0, currentDelay + timeDelta);
          updatedLayer.textAnimation = {
            ...l.textAnimation,
            delay: newDelay,
          };
        }

        if (!isDragging && l.boxAnimation && timeDelta !== 0) {
          const currentDelay = l.boxAnimation.delay || 0;
          const newDelay = Math.max(0, currentDelay + timeDelta);
          updatedLayer.boxAnimation = {
            ...l.boxAnimation,
            delay: newDelay,
          };
        }

        return updatedLayer;
      }
      return l;
    });

    this.setState({ layers: newLayers });

    if (this.props.onAnimationDurationChange && layer.hasAnimations) {
      this.props.onAnimationDurationChange(layerId, layer.animations);
    }

    if (
      this.props.onSaveNewAnimation &&
      layer.hasAnimations &&
      layer.animations &&
      layer.animations.length > 0
    ) {
      this.props.onSaveNewAnimation(layerId, layer.animations);
    }

    if (this.props.onLayerTimingChange) {
      this.props.onLayerTimingChange(layerId, {
        animations: layer.animations,
      });
    }

    if (!isDragging && timeDelta !== 0) {
      const updatedLayer = newLayers.find((l) => l.id === layerId);

      if (updatedLayer.textAnimation && this.props.onTextAnimationUpdate) {
        this.props.onTextAnimationUpdate(layerId, { delay: updatedLayer.textAnimation.delay });
      }

      if (updatedLayer.boxAnimation && this.props.onBoxAnimationUpdate) {
        this.props.onBoxAnimationUpdate(layerId, { delay: updatedLayer.boxAnimation.delay });
      }

      if (this.props.onLayerUpdate) {
        this.props.onLayerUpdate(updatedLayer);
      }
    }
  }

  showContextMenu(event, layerId) {
    event.preventDefault();
    event.stopPropagation();

    const { clientX, clientY } = event;
    const layer = this.state.layers.find((l) => l.id === layerId);
    if (!layer) return;

    const layerElement = event.target.closest(".timeline-layer");
    if (!layerElement) return;

    const layerRect = layerElement.getBoundingClientRect();
    const relativeX = clientX - layerRect.left;
    const relativePosition = Math.max(0, Math.min(1, relativeX / layerRect.width));
    const timePosition =
      (layer.startFrame + relativePosition * (layer.endFrame - layer.startFrame)) / this.state.fps;

    this.setState({
      contextMenu: {
        visible: true,
        x: clientX,
        y: clientY,
        layerId,
        timePosition: Math.max(0, timePosition),
      },
    });
  }

  hideContextMenu(event) {
    if (event && event.target.closest(".timeline-context-menu")) {
      return;
    }
    this.setState({ contextMenu: null });
  }

  addAnimation(layerId, animationType, timePosition) {
    const layer = this.state.layers.find((l) => l.id === layerId);
    if (!layer) return;

    if (animationType === "add-text-animation") {
      this.setState({ currentEditingLayer: { box: { id: layerId } } });
      this.openTextAnimationModal();
      return;
    }

    if (animationType === "add-box-animation") {
      this.setState({ currentEditingLayer: { box: { id: layerId } } });
      this.openBoxAnimationModal();
      return;
    }

    const keyframeList = this.createDefaultAnimation(animationType, timePosition);
    if (!keyframeList || keyframeList.length < 2) return;

    const updatedLayers = this.state.layers.map((l) => {
      if (l.id === layerId) {
        return {
          ...l,
          animations: keyframeList,
          keyframes: [],
          hasAnimations: true,
        };
      }
      return l;
    });

    this.setState({ layers: updatedLayers });

    if (this.props.onAnimationUpdate) {
      this.props.onAnimationUpdate(layerId, keyframeList);
    }

    if (this.props.onSaveNewAnimation) {
      this.props.onSaveNewAnimation(layerId, keyframeList);
    }
  }

  createDefaultAnimation(animationType, timePosition) {
    switch (animationType) {
      case "keyframe-custom":
        return [
          {
            positionx: "0%",
            positiony: "0%",
            opacity: 1,
            width: "25%",
            duration: 0,
          },
          {
            positionx: "50%",
            positiony: "50%",
            opacity: 0.5,
            width: "50%",
            duration: 1,
          },
        ];

      case "keyframe-morph":
        return [
          {
            positionx: "0%",
            positiony: "0%",
            opacity: 1,
            width: "25%",
            duration: 0,
          },
          {
            positionx: "25%",
            positiony: "25%",
            opacity: 1,
            width: "75%",
            duration: 2,
          },
        ];

      case "keyframe-combined":
        return [
          {
            positionx: "0%",
            positiony: "0%",
            opacity: 0,
            width: "25%",
            duration: 0,
          },
          {
            positionx: "75%",
            positiony: "75%",
            opacity: 1,
            width: "50%",
            duration: 1.5,
          },
        ];

      default:
        return [
          {
            positionx: "0%",
            positiony: "0%",
            opacity: 1,
            width: "25%",
            duration: 0,
          },
          {
            positionx: "50%",
            positiony: "50%",
            opacity: 1,
            width: "25%",
            duration: 1,
          },
        ];
    }
  }

  clearAllAnimations(layerId) {
    const layer = this.state.layers.find((l) => l.id === layerId);
    if (!layer) return;

    const updatedLayers = this.state.layers.map((l) => {
      if (l.id === layerId) {
        return {
          ...l,
          animations: [],
          keyframes: [],
          hasAnimations: false,
        };
      }
      return l;
    });

    this.setState({ layers: updatedLayers });

    if (this.props.onAnimationUpdate) {
      this.props.onAnimationUpdate(layerId, []);
    }
  }

  renderTimelineHeader() {
    const { zoom } = this.state;

    return (
      <div className="timeline-header">
        <div className="header-left">
          <button
            className="collapse-btn"
            onClick={() => this.setState({ showLayerPanel: !this.state.showLayerPanel })}
          >
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path
                d={svgPaths.p3d5bee80}
                stroke="#4A5565"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <span className="timeline-title">Timeline</span>
          <button
            className={`play-btn-header ${this.state.playing ? "playing" : ""}`}
            onClick={this.state.playing ? this.handlePause : this.handlePlay}
            title={this.state.playing ? "Pause" : "Play"}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
              {this.state.playing ? (
                <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
              ) : (
                <path d="M8 5v14l11-7z" />
              )}
            </svg>
          </button>
          <button
            className="show-timeline-btn"
            onClick={() => {
              if (this.props.onTimelineToggle) {
                this.props.onTimelineToggle();
              }
            }}
            title="Show all content on scene"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M2 3h12v10H2V3z"
                stroke="#4A5565"
                strokeWidth="1.33"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path d="M6 7l4 2-4 2V7z" fill="#4A5565" />
            </svg>
          </button>
        </div>
        <div className="header-center"></div>
        <div className="header-right">
          <button className="icon-btn">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d={svgPaths.pf7e8100}
                stroke="#4A5565"
                strokeWidth="1.33"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d={svgPaths.p28db2b80}
                stroke="#4A5565"
                strokeWidth="1.33"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <button className="icon-btn">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M4 6L8 10L12 6"
                stroke="#4A5565"
                strokeWidth="1.33"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <button className="icon-btn">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M12 10L8 6L4 10"
                stroke="#4A5565"
                strokeWidth="1.33"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>
    );
  }

  renderTimeRuler() {
    const { duration, fps, currentFrame, layers, zoom } = this.state;
    const totalSeconds = Math.ceil(duration / fps);
    const markers = [];

    for (let i = 0; i <= totalSeconds; i++) {
      const frame = i * fps;
      const leftPercent = Math.min(100, (frame / duration) * 100);
      const time = `${Math.floor(i / 60)}:${String(i % 60).padStart(2, "0")}`;

      markers.push(
        <div key={i} className="time-marker" style={{ left: `${leftPercent}%` }}>
          <span>{time}</span>
        </div>
      );
    }

    const playheadPercent = Math.min(100, (currentFrame / duration) * 100);

    const sceneEndTime = Math.max(...layers.map((l) => l.endFrame / fps), duration / fps);
    const sceneEndPercent = Math.min(100, ((sceneEndTime * fps) / duration) * 100);

    return (
      <div className="timeline-ruler" style={{ width: `${100 * zoom}%`, minWidth: "100%" }}>
        {markers}
        <div
          className="scene-duration-bar"
          style={{ width: `${sceneEndPercent}%` }}
          title={`Scene duration: ${sceneEndTime.toFixed(2)}s`}
        />
        <div
          className="playhead"
          style={{ left: `${playheadPercent}%` }}
          onMouseDown={this.startPlayheadDrag}
        >
          <div className="playhead-marker">
            <span className="playhead-time">
              {Math.floor(currentFrame / fps / 60)}:
              {Math.floor((currentFrame / fps) % 60)
                .toString()
                .padStart(2, "0")}
            </span>
          </div>
        </div>
        <div className="duration-controls">
          <button
            className="duration-btn"
            onClick={() => this.adjustSceneDuration(-1)}
            title="Decrease scene duration by 1 second"
          >
            -
          </button>
          <span className="duration-display" title="Scene duration">
            {(duration / fps).toFixed(1)}s
          </span>
          <button
            className="duration-btn"
            onClick={() => this.adjustSceneDuration(1)}
            title="Increase scene duration by 1 second"
          >
            +
          </button>
        </div>
      </div>
    );
  }

  renderLayerPanel() {
    const { layers, selectedLayers, showLayerPanel } = this.state;

    return (
      <div className="timeline-layers">
        {layers
          .slice()
          .reverse()
          .map((layer, reverseIndex) => {
            const index = layers.length - 1 - reverseIndex;
            const isSelected = selectedLayers.includes(layer.id);

            return (
              <div
                key={layer.id}
                className={`layer-row ${isSelected ? "selected" : ""} ${layer.visible ? "" : "hidden"} ${layer.locked ? "locked" : ""}`}
                onClick={() => this.handleLayerSelect(layer.id)}
              >
                {/* <button
                  className="visibility-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    this.toggleLayerVisibility(layer.id);
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path
                      d={svgPaths.pb85f580}
                      stroke="#4A5565"
                      strokeWidth="1.33"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    {layer.visible && (
                      <path
                        d={svgPaths.p36446d40}
                        stroke="#4A5565"
                        strokeWidth="1.33"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    )}
                  </svg>
                </button> */}
                <button
                  className="lock-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    this.toggleLayerLock(layer.id);
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path
                      d={svgPaths.p341e6280}
                      stroke="#6A7282"
                      strokeWidth="1.33"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                <div className="layer-icon">{this.getLayerIconSVG(layer.type)}</div>
                <span className="layer-name">{layer.name}</span>
                {layer.starred && <div className="star-indicator"></div>}
              </div>
            );
          })}
      </div>
    );
  }

  getLayerIconSVG(type) {
    switch (type) {
      case "image":
        return (
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path
              d={svgPaths.p3b86be00}
              stroke="#6A7282"
              strokeWidth="1.33"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        );
      case "text":
        return (
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path
              d="M0.666667 2.66667V1.33333C0.666667 1.15652 0.736905 0.986953 0.861929 0.861929C0.986953 0.736905 1.15652 0.666667 1.33333 0.666667H10.6667C10.8435 0.666667 11.013 0.736905 11.1381 0.861929C11.2631 0.986953 11.3333 1.15652 11.3333 1.33333V2.66667"
              stroke="#6A7282"
              strokeWidth="1.33"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M0.666667 0.666667V11.3333"
              stroke="#6A7282"
              strokeWidth="1.33"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M0.666667 0.666667H4.66667"
              stroke="#6A7282"
              strokeWidth="1.33"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        );
      case "element":
      case "shape":
        return (
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path
              d={svgPaths.p10fc0e80}
              stroke="#6A7282"
              strokeWidth="1.33"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        );
      case "audio":
        return (
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path
              d={svgPaths.p1691cc0}
              fill="#BC6DFA"
              stroke="#BC6DFA"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        );
      case "scene-audio":
        return (
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path
              d={svgPaths.p1691cc0}
              fill="#22C55E"
              stroke="#22C55E"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        );
      default:
        return (
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d={svgPaths.p16736e80} stroke="#6A7282" strokeWidth="1.5" />
          </svg>
        );
    }
  }

  getAnimationIcon(type) {
    const iconStyle = {
      width: "12px",
      height: "12px",
      fill: "white",
      stroke: "none",
    };

    const animationType = this.getAnimationCategory(type);

    switch (animationType) {
      case "box":
        return (
          <svg viewBox="0 0 16 16" style={iconStyle}>
            <path d="M2 3h12v10H2V3zm2 2v6h8V5H4z" />
          </svg>
        );
      case "text":
        return (
          <svg viewBox="0 0 16 16" style={iconStyle}>
            <path d="M1 3h14v2H9v8H7V5H1V3z" />
            <path d="M10 9h4v2h-4V9zm0 3h3v1h-3v-1z" />
          </svg>
        );
      case "position":
        return (
          <svg viewBox="0 0 16 16" style={iconStyle}>
            <path d="M8 1l3 3-3 3V5H4V3h4V1zm5 7l-3-3v2H6v2h4v2l3-3z" />
          </svg>
        );
      case "opacity":
        return (
          <svg viewBox="0 0 16 16" style={iconStyle}>
            <path d="M8 2C4.7 2 2 4.7 2 8s2.7 6 6 6 6-2.7 6-6-2.7-6-6-6zm0 10c-2.2 0-4-1.8-4-4s1.8-4 4-4v8z" />
          </svg>
        );
      case "scale":
        return (
          <svg viewBox="0 0 16 16" style={iconStyle}>
            <path d="M3 3h10v10H3V3zm2 2v6h6V5H5z" />
            <path d="M1 1h2v2H1V1zm12 0h2v2h-2V1zM1 13h2v2H1v-2zm12 0h2v2h-2v-2z" />
          </svg>
        );
      case "rotation":
        return (
          <svg viewBox="0 0 16 16" style={iconStyle}>
            <path d="M8 2C5.8 2 4 3.8 4 6v1H2l3 3 3-3H6V6c0-1.1.9-2 2-2s2 .9 2 2-.9 2-2 2v2c2.2 0 4-1.8 4-4s-1.8-4-4-4z" />
          </svg>
        );
      case "combined":
        return (
          <svg viewBox="0 0 16 16" style={iconStyle}>
            <circle cx="4" cy="4" r="2" />
            <circle cx="12" cy="4" r="2" />
            <circle cx="4" cy="12" r="2" />
            <circle cx="12" cy="12" r="2" />
            <circle cx="8" cy="8" r="1" />
          </svg>
        );
      default:
        return (
          <svg viewBox="0 0 16 16" style={iconStyle}>
            <path d="M8 2l2.5 5H15l-4 3.5 1.5 5L8 12l-4.5 3.5L5 10 1 6.5h4.5L8 2z" />
          </svg>
        );
    }
  }

  getAnimationCategory(type) {
    if (!type || typeof type !== "string") {
      return "keyframe";
    }

    if (type.startsWith("box-")) return "box";
    if (type.startsWith("text-")) return "text";
    switch (type) {
      case "position":
      case "opacity":
      case "scale":
      case "rotation":
        return type;
      case "combined":
        return "combined";
      default:
        return "animation";
    }
  }

  getLayerIcon(type) {
    switch (type) {
      case "image":
        return "image";
      case "text":
        return "text_fields";
      case "video":
        return "videocam";
      case "audio":
        return "volume_up";
      case "shape":
        return "crop_square";
      default:
        return "layers";
    }
  }

  renderTimelineTracks() {
    const { layers, selectedLayers, duration, zoom } = this.state;
    const totalTimelineDuration = duration / this.state.fps;

    return (
      <div className="timeline-tracks" style={{ width: `${100 * zoom}%`, minWidth: "100%" }}>
        {layers
          .slice()
          .reverse()
          .map((layer, reverseIndex) => {
            const index = layers.length - 1 - reverseIndex;
            const isSelected = selectedLayers.includes(layer.id);
            const leftPercent = (layer.startFrame / duration) * 100;
            const widthPercent = ((layer.endFrame - layer.startFrame) / duration) * 100;

            return (
              <div key={layer.id} className="timeline-layer-group">
                {/* Main Layer Track */}
                <div className={`timeline-track layer-main-track ${isSelected ? "selected" : ""}`}>
                  <div
                    className={`timeline-layer ${layer.canEdit ? "editable" : "non-editable"} ${layer.locked ? "locked" : ""} ${layer.visible ? "" : "hidden"}`}
                    data-layer-id={layer.id}
                    style={{
                      left: `${Math.max(0, Math.min(leftPercent, 100))}%`,
                      width: `${Math.max(1, Math.min(widthPercent, 100 - leftPercent))}%`,
                      backgroundColor: this.getLayerColor(layer),
                      opacity: layer.visible ? 1 : 0.5,
                      border: "2px solid rgba(255,255,255,0.3)",
                    }}
                    onClick={() => this.handleLayerSelect(layer.id)}
                    onDoubleClick={(e) => this.handleLayerClick(e, layer.id)}
                    onContextMenu={(e) => this.showContextMenu(e, layer.id)}
                  >
                    <div className="layer-content">
                      <div className="layer-icon">{this.getLayerIconSVG(layer.type)}</div>
                      <span className="layer-title">{layer.name}</span>
                      <span className="layer-timing">
                        {(layer.startFrame / this.state.fps).toFixed(1)}s -{" "}
                        {(layer.endFrame / this.state.fps).toFixed(1)}s
                      </span>
                    </div>

                    {this.renderWaveform(layer)}

                    {/* Text Animation */}
                    {layer.hasTextAnimation && (
                      <div className="text-box-animations-container">
                        {this.renderTextAnimation(layer)}
                      </div>
                    )}

                    {/* Box Animation */}
                    {layer.hasBoxAnimation && (
                      <div className="text-box-animations-container">
                        {this.renderBoxAnimation(layer)}
                      </div>
                    )}

                    {/* Resize handles */}
                    {layer.canEdit && !layer.locked && (
                      <>
                        <div className="resize-handle resize-left"></div>
                        <div className="resize-handle resize-right"></div>
                      </>
                    )}

                    {layer.locked && (
                      <div className="layer-lock-indicator">
                        <svg width="10" height="10" viewBox="0 0 16 16" fill="none">
                          <path
                            d={svgPaths.p341e6280}
                            stroke="#f39c12"
                            strokeWidth="1.33"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
      </div>
    );
  }

  generateFrequencySpectrum(audioData, binCount = 64) {
    const spectrum = [];
    const audioLength = (audioData && audioData.endTime - audioData.startTime) || 10;

    for (let i = 0; i < binCount; i++) {
      const frequencyRatio = i / binCount;
      const frequency = frequencyRatio * frequencyRatio * 20000; // Logarithmic frequency scale (0-20kHz)

      // Simulate realistic frequency spectrum
      let magnitude = 0;

      // Low frequencies (20-250 Hz) - Bass/Sub-bass
      if (frequency < 250) {
        magnitude = 0.8 - (frequency / 250) * 0.3;
        magnitude += Math.sin(audioLength * 0.5 + frequency / 100) * 0.2;
      }
      // Low-mid frequencies (250-2000 Hz) - Fundamental speech
      else if (frequency < 2000) {
        magnitude = 0.9 - (Math.abs(frequency - 800) / 2000) * 0.3;
        magnitude += Math.sin(audioLength * 1.2 + frequency / 400) * 0.25;
      }
      // Mid frequencies (2-6 kHz) - Voice clarity/presence
      else if (frequency < 6000) {
        magnitude = 0.7 - (Math.abs(frequency - 3000) / 3000) * 0.4;
        magnitude += Math.sin(audioLength * 2 + frequency / 800) * 0.3;
      }
      // High-mid frequencies (6-12 kHz) - Consonants/detail
      else if (frequency < 12000) {
        magnitude = 0.5 - ((frequency - 6000) / 6000) * 0.3;
        magnitude += Math.sin(audioLength * 3 + frequency / 1200) * 0.2;
      }
      // High frequencies (12-20 kHz) - Air/brilliance
      else {
        magnitude = 0.2 - ((frequency - 12000) / 8000) * 0.15;
        magnitude += Math.sin(audioLength * 4 + frequency / 2000) * 0.1;
      }

      // Add some realistic variation and noise
      magnitude += (Math.random() - 0.5) * 0.15;
      magnitude *= 0.8 + Math.random() * 0.4; // Natural variation

      // Ensure realistic bounds
      magnitude = Math.max(0.05, Math.min(1, magnitude));

      spectrum.push(magnitude);
    }

    return spectrum;
  }

  renderWaveform(layer) {
    if (layer.type !== "scene-audio" || !layer.audioData) return null;

    const audioData = layer.audioData;
    const hasAudio = audioData && (audioData.file || audioData.url || audioData.src);

    if (!hasAudio) {
      return (
        <div
          className="spectrum-container"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            pointerEvents: "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              color: "#9CA3AF",
              fontSize: "9px",
              opacity: 0.5,
              fontWeight: "400",
            }}
          >
            No Audio Spectrum
          </div>
        </div>
      );
    }

    // Generate frequency spectrum data for smooth continuous spectrum
    const spectrumData = this.generateFrequencySpectrum(audioData, 100);

    return (
      <div
        className="low-profile-spectrum-container"
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          pointerEvents: "none",
          overflow: "hidden",
        }}
      >
        <svg
          width="100%"
          height="100%"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
          }}
        >
          <defs>
            {/* Green audio spectrum gradient */}
            <linearGradient id="spectrumGrad" x1="0%" y1="100%" x2="0%" y2="0%">
              <stop offset="0%" stopColor="#34D399" stopOpacity="0.7" />
              <stop offset="25%" stopColor="#6EE7B7" stopOpacity="0.8" />
              <stop offset="50%" stopColor="#A7F3D0" stopOpacity="0.9" />
              <stop offset="75%" stopColor="#D1FAE5" stopOpacity="0.8" />
              <stop offset="100%" stopColor="#ECFDF5" stopOpacity="0.7" />
            </linearGradient>

            {/* Subtle glow effect */}
            <filter id="spectrumGlow">
              <feGaussianBlur stdDeviation="0.2" result="coloredBlur" />
              <feMerge>
                <feMergeNode in="coloredBlur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          {/* Center line - main reference */}
          <line
            x1="0"
            y1="50"
            x2="100"
            y2="50"
            stroke="rgba(156, 163, 175, 0.5)"
            strokeWidth="0.1"
          />

          {/* Upper spectrum (above center line) - LOW PROFILE */}
          <path
            d={(() => {
              let pathData = `M 0 50`; // Start at center-left

              // Draw upper spectrum outline with reduced height
              spectrumData.forEach((magnitude, i) => {
                const x = (i / spectrumData.length) * 100;
                const spectrumHeight = magnitude * 18; // Reduced to 18% max height (low profile)
                const y = 50 - spectrumHeight; // Go up from center
                pathData += ` L ${x} ${y}`;
              });

              // Close path back to center line
              pathData += ` L 100 50 Z`;
              return pathData;
            })()}
            fill="url(#spectrumGrad)"
            opacity="0.7"
            filter="url(#spectrumGlow)"
          />

          {/* Lower spectrum (below center line - mirrored) - LOW PROFILE */}
          <path
            d={(() => {
              let pathData = `M 0 50`; // Start at center-left

              // Draw lower spectrum outline (mirrored) with reduced height
              spectrumData.forEach((magnitude, i) => {
                const x = (i / spectrumData.length) * 100;
                const spectrumHeight = magnitude * 18; // Reduced to 18% max height (low profile)
                const y = 50 + spectrumHeight; // Go down from center
                pathData += ` L ${x} ${y}`;
              });

              // Close path back to center line
              pathData += ` L 100 50 Z`;
              return pathData;
            })()}
            fill="url(#spectrumGrad)"
            opacity="0.7"
            filter="url(#spectrumGlow)"
          />

          {/* Upper spectrum smooth outline */}
          <path
            d={(() => {
              let pathData = `M 0 50`;

              spectrumData.forEach((magnitude, i) => {
                const x = (i / spectrumData.length) * 100;
                const spectrumHeight = magnitude * 18;
                const y = 50 - spectrumHeight;

                if (i === 0) {
                  pathData = `M ${x} ${y}`;
                } else {
                  pathData += ` L ${x} ${y}`;
                }
              });

              return pathData;
            })()}
            fill="none"
            stroke="rgba(156, 163, 175, 0.6)"
            strokeWidth="0.12"
            opacity="0.7"
            strokeLinecap="round"
            strokeLinejoin="round"
          />

          {/* Lower spectrum smooth outline */}
          <path
            d={(() => {
              let pathData = `M 0 50`;

              spectrumData.forEach((magnitude, i) => {
                const x = (i / spectrumData.length) * 100;
                const spectrumHeight = magnitude * 18;
                const y = 50 + spectrumHeight;

                if (i === 0) {
                  pathData = `M ${x} ${y}`;
                } else {
                  pathData += ` L ${x} ${y}`;
                }
              });

              return pathData;
            })()}
            fill="none"
            stroke="rgba(156, 163, 175, 0.6)"
            strokeWidth="0.12"
            opacity="0.7"
            strokeLinecap="round"
            strokeLinejoin="round"
          />

          {/* Frequency range labels */}
          <text x="5" y="8" fontSize="1.8" fill="rgba(55, 65, 81, 0.8)" fontWeight="500">
            20Hz
          </text>
          <text x="25" y="8" fontSize="1.8" fill="rgba(55, 65, 81, 0.8)" fontWeight="500">
            200Hz
          </text>
          <text x="45" y="8" fontSize="1.8" fill="rgba(55, 65, 81, 0.8)" fontWeight="500">
            2kHz
          </text>
          <text x="70" y="8" fontSize="1.8" fill="rgba(55, 65, 81, 0.8)" fontWeight="500">
            8kHz
          </text>
          <text x="90" y="8" fontSize="1.8" fill="rgba(55, 65, 81, 0.8)" fontWeight="500">
            20kHz
          </text>
        </svg>

        {/* Audio info */}
        <div
          style={{
            position: "absolute",
            top: "3px",
            left: "4px",
            fontSize: "6px",
            color: "rgba(55, 65, 81, 0.9)",
            fontWeight: "600",
            textShadow: "0 1px 2px rgba(255,255,255,0.8)",
          }}
        >
          {audioData.name || "Audio"} • SPECTRUM
        </div>

        {/* Duration */}
        <div
          style={{
            position: "absolute",
            bottom: "1px",
            left: "4px",
            fontSize: "5px",
            color: "rgba(55, 65, 81, 0.8)",
            fontWeight: "500",
          }}
        >
          {((audioData.endTime || 0) - (audioData.startTime || 0)).toFixed(1)}s
        </div>
      </div>
    );
  }

  getLayerColor(layer) {
    if (layer && layer.type === "scene-audio") {
      return "#22C55E";
    }
    const defaultColor = this.props.defaultLayerColor || "#4A90E2";
    return defaultColor;
  }

  getVariedLayerColor(index) {
    const colors = [
      "#FF6B6B",
      "#4ECDC4",
      "#45B7D1",
      "#96CEB4",
      "#FFEAA7",
      "#DDA0DD",
      "#98D8C8",
      "#F7DC6F",
      "#BB8FCE",
      "#85C1E9",
      "#F8C471",
      "#82E0AA",
    ];
    return colors[index % colors.length];
  }

  renderTextAnimation(layer) {
    const textAnimation = layer.textAnimation;
    if (!textAnimation) return null;

    const delay = Number(textAnimation.delay) || 0;
    const duration = Number(textAnimation.duration) || 2;
    const totalTimelineDuration = this.state.duration / this.state.fps;

    const layerStartPercent = ((layer.startFrame || 0) / this.state.duration) * 100;
    const layerWidthPercent = ((layer.endFrame - layer.startFrame) / this.state.duration) * 100;

    const startTime = delay;
    const endTime = delay + duration;

    const startTimelinePosition = (startTime / totalTimelineDuration) * 100;
    const endTimelinePosition = (endTime / totalTimelineDuration) * 100;

    const relativeStartPosition =
      layerWidthPercent > 0
        ? ((startTimelinePosition - layerStartPercent) / layerWidthPercent) * 100
        : 0;
    const relativeEndPosition =
      layerWidthPercent > 0
        ? ((endTimelinePosition - layerStartPercent) / layerWidthPercent) * 100
        : 0;

    if (relativeStartPosition > 100 || relativeEndPosition < 0) return null;

    const clampedStartPosition = Math.max(0, relativeStartPosition);
    const clampedEndPosition = Math.min(100, relativeEndPosition);
    const clampedWidth = clampedEndPosition - clampedStartPosition;

    const animName = textAnimation.animation?.name || "Text";
    const isWide = clampedWidth > 8;

    return (
      <div
        className="text-animation-block"
        style={{
          position: "absolute",
          left: `${clampedStartPosition}%`,
          width: `${Math.max(3, clampedWidth)}%`,
          height: "14px",
          top: "1px",
          background:
            "linear-gradient(90deg, rgba(99,102,241,0.95) 0%, rgba(99,102,241,0.6) 55%, rgba(99,102,241,0.1) 100%)",
          borderLeft: "2.5px solid #6366f1",
          borderTop: "1px solid rgba(129,140,248,0.5)",
          borderBottom: "1px solid rgba(129,140,248,0.5)",
          borderRight: "none",
          borderRadius: "3px 0 0 3px",
          clipPath: "polygon(0 0, calc(100% - 6px) 0, 100% 50%, calc(100% - 6px) 100%, 0 100%)",
          cursor: "grab",
          zIndex: 8,
          display: "flex",
          alignItems: "center",
          overflow: "hidden",
          boxShadow: "0 1px 4px rgba(99,102,241,0.35)",
        }}
        title={`Text Animation: ${animName} | Start: ${(delay || 0).toFixed(2)}s | Duration: ${(duration || 0).toFixed(2)}s`}
        onClick={(e) => {
          e.stopPropagation();
          this.handleSeek(delay * this.state.fps);
        }}
        onDoubleClick={(e) => {
          e.stopPropagation();
          // Get fresh data from Redux store instead of stale local state
          const freshBox = this.props.boxes[layer.id];
          this.setState({ currentEditingLayer: { box: freshBox || { id: layer.id } } });
          this.openTextAnimationModal();
        }}
        onMouseDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
          this.startTextAnimationDrag(e, layer);
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "2px",
            paddingLeft: "3px",
            overflow: "hidden",
            flexShrink: 0,
          }}
        >
          <svg width="7" height="7" viewBox="0 0 8 8" fill="none" style={{ flexShrink: 0 }}>
            <path
              d="M1 5L4 2L7 5"
              stroke="white"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          {isWide && (
            <span
              style={{
                fontSize: "8px",
                fontWeight: "700",
                color: "white",
                textShadow: "0 1px 2px rgba(0,0,0,0.6)",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                letterSpacing: "0.1px",
              }}
            >
              {animName}
            </span>
          )}
        </div>
      </div>
    );
  }

  renderBoxAnimation(layer) {
    const boxAnimation = layer.boxAnimation;
    if (!boxAnimation) return null;

    const delay = Number(boxAnimation.delay) || 0;
    const duration = Number(boxAnimation.duration) || 2;
    const totalTimelineDuration = this.state.duration / this.state.fps;

    const layerStartPercent = ((layer.startFrame || 0) / this.state.duration) * 100;
    const layerWidthPercent = ((layer.endFrame - layer.startFrame) / this.state.duration) * 100;

    const startTime = delay;
    const endTime = delay + duration;

    const startTimelinePosition = (startTime / totalTimelineDuration) * 100;
    const endTimelinePosition = (endTime / totalTimelineDuration) * 100;

    const relativeStartPosition =
      layerWidthPercent > 0
        ? ((startTimelinePosition - layerStartPercent) / layerWidthPercent) * 100
        : 0;
    const relativeEndPosition =
      layerWidthPercent > 0
        ? ((endTimelinePosition - layerStartPercent) / layerWidthPercent) * 100
        : 0;

    if (relativeStartPosition > 100 || relativeEndPosition < 0) return null;

    const clampedStartPosition = Math.max(0, relativeStartPosition);
    const clampedEndPosition = Math.min(100, relativeEndPosition);
    const clampedWidth = clampedEndPosition - clampedStartPosition;

    const animName = boxAnimation.animation?.name || "Box";
    const isWide = clampedWidth > 8;

    return (
      <div
        className="box-animation-block"
        style={{
          position: "absolute",
          left: `${clampedStartPosition}%`,
          width: `${Math.max(3, clampedWidth)}%`,
          height: "14px",
          top: "17px",
          background:
            "linear-gradient(90deg, rgba(16,185,129,0.95) 0%, rgba(16,185,129,0.6) 55%, rgba(16,185,129,0.1) 100%)",
          borderLeft: "2.5px solid #10b981",
          borderTop: "1px solid rgba(52,211,153,0.5)",
          borderBottom: "1px solid rgba(52,211,153,0.5)",
          borderRight: "none",
          borderRadius: "3px 0 0 3px",
          clipPath: "polygon(0 0, calc(100% - 6px) 0, 100% 50%, calc(100% - 6px) 100%, 0 100%)",
          cursor: "grab",
          zIndex: 8,
          display: "flex",
          alignItems: "center",
          overflow: "hidden",
          boxShadow: "0 1px 4px rgba(16,185,129,0.35)",
        }}
        title={`Box Animation: ${animName} | Start: ${(delay || 0).toFixed(2)}s | Duration: ${(duration || 0).toFixed(2)}s`}
        onClick={(e) => {
          e.stopPropagation();
          this.handleSeek(delay * this.state.fps);
        }}
        onDoubleClick={(e) => {
          e.stopPropagation();
          // Get fresh data from Redux store instead of stale local state
          const freshBox = this.props.boxes[layer.id];
          this.setState({ currentEditingLayer: { box: freshBox || { id: layer.id } } });
          this.openBoxAnimationModal();
        }}
        onMouseDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
          this.startBoxAnimationDrag(e, layer);
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "2px",
            paddingLeft: "3px",
            overflow: "hidden",
            flexShrink: 0,
          }}
        >
          <svg width="7" height="7" viewBox="0 0 8 8" fill="none" style={{ flexShrink: 0 }}>
            <path
              d="M1 5L4 2L7 5"
              stroke="white"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          {isWide && (
            <span
              style={{
                fontSize: "8px",
                fontWeight: "700",
                color: "white",
                textShadow: "0 1px 2px rgba(0,0,0,0.6)",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                letterSpacing: "0.1px",
              }}
            >
              {animName}
            </span>
          )}
        </div>
      </div>
    );
  }

  startTextAnimationDrag(event, layer) {
    if (!layer.textAnimation || this.isDraggingAnimation) return;

    const layerElement = event.target.closest(".timeline-layer");
    if (!layerElement) return;

    this.isDraggingAnimation = true;
    const animationElement = event.target;
    animationElement.style.cursor = "grabbing";
    animationElement.style.opacity = "0.8";

    const layerRect = layerElement.getBoundingClientRect();
    const startX = event.clientX;
    const currentDelay = layer.textAnimation.delay || 0;
    const layerStartTime = (layer.startFrame || 0) / this.state.fps;
    const layerEndTime = (layer.endFrame || this.state.duration) / this.state.fps;
    const layerDuration = layerEndTime - layerStartTime;

    const handleMouseMove = (e) => {
      e.stopPropagation();
      const deltaX = e.clientX - startX;
      const relativeMovement = deltaX / layerRect.width;
      const timeMovement = relativeMovement * layerDuration;
      const newDelay = Math.max(0, currentDelay + timeMovement);

      this.updateTextAnimationDelay(layer.id, newDelay);
    };

    const handleMouseUp = (e) => {
      e.stopPropagation();
      this.isDraggingAnimation = false;
      animationElement.style.cursor = "grab";
      animationElement.style.opacity = "1";
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  }

  startBoxAnimationDrag(event, layer) {
    if (!layer.boxAnimation || this.isDraggingAnimation) return;

    const layerElement = event.target.closest(".timeline-layer");
    if (!layerElement) return;

    this.isDraggingAnimation = true;
    const animationElement = event.target;
    animationElement.style.cursor = "grabbing";
    animationElement.style.opacity = "0.8";

    const layerRect = layerElement.getBoundingClientRect();
    const startX = event.clientX;
    const currentDelay = layer.boxAnimation.delay || 0;
    const layerStartTime = (layer.startFrame || 0) / this.state.fps;
    const layerEndTime = (layer.endFrame || this.state.duration) / this.state.fps;
    const layerDuration = layerEndTime - layerStartTime;

    const handleMouseMove = (e) => {
      e.stopPropagation();
      const deltaX = e.clientX - startX;
      const relativeMovement = deltaX / layerRect.width;
      const timeMovement = relativeMovement * layerDuration;
      const newDelay = Math.max(0, currentDelay + timeMovement);

      this.updateBoxAnimationDelay(layer.id, newDelay);
    };

    const handleMouseUp = (e) => {
      e.stopPropagation();
      this.isDraggingAnimation = false;
      animationElement.style.cursor = "grab";
      animationElement.style.opacity = "1";
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  }

  updateTextAnimationDelay(layerId, newDelay) {
    const updatedLayers = this.state.layers.map((layer) => {
      if (layer.id === layerId && layer.textAnimation) {
        return {
          ...layer,
          textAnimation: {
            ...layer.textAnimation,
            delay: newDelay,
          },
        };
      }
      return layer;
    });

    this.setState({ layers: updatedLayers });

    if (this.props.onTextAnimationUpdate) {
      // Set flag to prevent initializeLayers during animation dragging
      this.isFinalizingDrag = true;
      this.props.onTextAnimationUpdate(layerId, { delay: newDelay });
      // Clear flag after a brief delay to allow prop updates to complete
      setTimeout(() => {
        this.isFinalizingDrag = false;
      }, 50);
    }

    if (this.props.onLayerUpdate) {
      const updatedLayer = updatedLayers.find((l) => l.id === layerId);
      this.props.onLayerUpdate(updatedLayer);
    }
  }

  updateBoxAnimationDelay(layerId, newDelay) {
    const updatedLayers = this.state.layers.map((layer) => {
      if (layer.id === layerId && layer.boxAnimation) {
        return {
          ...layer,
          boxAnimation: {
            ...layer.boxAnimation,
            delay: newDelay,
          },
        };
      }
      return layer;
    });

    this.setState({ layers: updatedLayers });

    if (this.props.onBoxAnimationUpdate) {
      // Set flag to prevent initializeLayers during animation dragging
      this.isFinalizingDrag = true;
      this.props.onBoxAnimationUpdate(layerId, { delay: newDelay });
      // Clear flag after a brief delay to allow prop updates to complete
      setTimeout(() => {
        this.isFinalizingDrag = false;
      }, 50);
    }

    if (this.props.onLayerUpdate) {
      const updatedLayer = updatedLayers.find((l) => l.id === layerId);
      this.props.onLayerUpdate(updatedLayer);
    }
  }

  toggleLayerPanel() {
    this.setState((prevState) => ({
      showLayerPanel: !prevState.showLayerPanel,
    }));
  }

  handleLayerClick(_event, _layerId) {
    return;
  }

  showAnimationSelectionDialog(x, y, layerId, timePosition) {
    const animationTypes = [];

    this.setState({
      animationSelectionDialog: {
        visible: true,
        x: Math.min(x, window.innerWidth - 300),
        y: Math.min(y, window.innerHeight - 200),
        layerId,
        timePosition,
        animationTypes,
        selectedType: animationTypes[0].id,
      },
    });
  }

  hideAnimationSelectionDialog() {
    this.setState({ animationSelectionDialog: null });
  }

  handleAnimationTypeSelection(animationType) {
    const { animationSelectionDialog } = this.state;
    if (!animationSelectionDialog) return;

    this.addAnimation(
      animationSelectionDialog.layerId,
      animationType,
      animationSelectionDialog.timePosition
    );
    this.hideAnimationSelectionDialog();
  }

  adjustSceneDuration(direction) {
    const { duration, fps, layers } = this.state;
    const currentDurationSeconds = duration / fps;
    const newDurationSeconds = Math.max(1, currentDurationSeconds + direction);
    const newDuration = newDurationSeconds * fps;

    const syncedLayers = layers.map((layer) => {
      if (layer.hasAnimations && layer.animations) {
        const lastAnimationTime = Math.max(...layer.animations.map((a) => a.duration || 0));

        if (lastAnimationTime > newDurationSeconds) {
          const scaleFactor =
            newDurationSeconds / Math.max(lastAnimationTime, currentDurationSeconds);
          const scaledAnimations = layer.animations.map((anim) => ({
            ...anim,
            duration: anim.duration * scaleFactor,
          }));

          return {
            ...layer,
            animations: scaledAnimations,
            keyframes: [],
            endFrame: Math.min(layer.endFrame, newDuration),
          };
        }
      }

      return {
        ...layer,
        endFrame: Math.min(layer.endFrame, newDuration),
      };
    });

    this.setState(
      {
        duration: newDuration,
        layers: syncedLayers,
      },
      () => {
        if (this.props.onSceneDurationChange) {
          this.props.onSceneDurationChange(newDurationSeconds);
        }

        if (this.props.onAnimationSync) {
          this.props.onAnimationSync(syncedLayers);
        }
      }
    );
  }

  syncAnimationWithScene() {
    const { duration, fps, layers } = this.state;

    if (!Array.isArray(layers) || layers.length === 0) return;

    const animationEndTimes = layers
      .filter((l) => l && l.hasAnimations && Array.isArray(l.animations))
      .flatMap((l) => {
        let cumulativeTime = 0;
        return l.animations.map((a) => {
          if (!a || typeof a !== "object") return 0;
          const animDuration = typeof a.duration === "number" && a.duration > 0 ? a.duration : 0.5;
          const animDelay = typeof a.delay === "number" && a.delay >= 0 ? a.delay : 0;
          cumulativeTime += animDuration;
          return cumulativeTime + animDelay;
        });
      });

    const layerEndTimes = layers.map((l) => {
      if (!l) return 0;
      return (l.endFrame || 0) / (fps || 30);
    });

    const longestAnimation = Math.max(...animationEndTimes, 0);
    const longestLayer = Math.max(...layerEndTimes, 0);
    const longestTime = Math.max(longestAnimation, longestLayer);

    if (longestTime > 0) {
      const currentDurationSeconds = duration / (fps || 30);

      if (longestTime > currentDurationSeconds) {
        const newDurationSeconds = Math.max(longestTime + 2, 5);
        const newDuration = newDurationSeconds * (fps || 30);

        this.setState({ duration: newDuration }, () => {
          if (this.props.onSceneDurationChange) {
            this.props.onSceneDurationChange(newDurationSeconds);
          }
        });
      }
    }
  }

  renderTimelineFooter() {
    const { duration, fps, currentFrame, playing } = this.state;
    const currentTime = currentFrame / fps;
    const totalTime = duration / fps;

    return (
      <div className="timeline-footer">
        <div className="footer-left">
          <button
            className={`play-btn ${playing ? "playing" : ""}`}
            onClick={playing ? this.handlePause : this.handlePlay}
            title={playing ? "Pause" : "Play"}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
              {playing ? <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" /> : <path d="M8 5v14l11-7z" />}
            </svg>
          </button>
          <button
            className="stop-btn"
            onClick={() => {
              this.handlePause();
              this.handleSeek(0);
            }}
            title="Stop and reset"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
              <path d="M6 6h12v12H6z" />
            </svg>
          </button>
          <span className="timeline-time">
            {Math.floor(currentTime / 60)}:
            {Math.floor(currentTime % 60)
              .toString()
              .padStart(2, "0")}{" "}
            / {Math.floor(totalTime / 60)}:
            {Math.floor(totalTime % 60)
              .toString()
              .padStart(2, "0")}
          </span>
          <div className="zoom-controls">
            <span className="zoom-label">Zoom:</span>
            <input
              type="range"
              min="0.25"
              max="4"
              step="0.05"
              value={this.state.zoom}
              className="zoom-slider"
              onChange={(e) => this.setState({ zoom: parseFloat(e.target.value) })}
            />
            <span className="zoom-text">{Math.round(this.state.zoom * 100)}%</span>
          </div>
        </div>
      </div>
    );
  }

  renderContextMenu() {
    const { contextMenu } = this.state;

    if (!contextMenu?.visible) return null;

    // Regular layer context menu
    const animationTypes = [
      {
        category: "Animation Options",
        items: [
          {
            id: "add-text-animation",
            label: "Add Text animation",
            icon: "text_fields",
            type: "text",
          },
          { id: "add-box-animation", label: "Add Box animation", icon: "widgets", type: "box" },
        ],
      },
    ];

    return (
      <>
        <div
          className="context-menu-overlay"
          onClick={this.hideContextMenu}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 999,
          }}
        />
        <div
          className="timeline-context-menu"
          style={{
            position: "fixed",
            left: Math.min(contextMenu.x, window.innerWidth - 200),
            top: Math.min(contextMenu.y, window.innerHeight - 300),
            zIndex: 1001,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="context-menu-header">Add Animation</div>
          {animationTypes.map((category) => (
            <div key={category.category} className="context-menu-category">
              <div className="context-menu-category-header">{category.category}</div>
              {category.items.map((item) => (
                <div
                  key={item.id}
                  className={`context-menu-item animation-type-${item.type}`}
                  onClick={() => {
                    this.addAnimation(contextMenu.layerId, item.id, contextMenu.timePosition);
                    this.hideContextMenu();
                  }}
                >
                  <i className="material-icons">{item.icon}</i>
                  <span>{item.label}</span>
                  <span className="animation-badge">{item.type}</span>
                </div>
              ))}
            </div>
          ))}

          {/* Add delete option if layer has animations */}
          {(() => {
            const layer = this.state.layers.find((l) => l.id === contextMenu.layerId);
            if (layer && layer.hasAnimations && layer.animations && layer.animations.length > 0) {
              return (
                <div className="context-menu-category">
                  <div className="context-menu-category-header">Layer Actions</div>
                  <div
                    className="context-menu-item"
                    onClick={() => {
                      this.clearAllAnimations(contextMenu.layerId);
                      this.hideContextMenu();
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                      <path d="M2.5 1a1 1 0 0 0-1 1v1a1 1 0 0 0 1 1H3v9a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V4h.5a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1H10a1 1 0 0 0-1-1H7a1 1 0 0 0-1 1H2.5zm3 4a.5.5 0 0 1 .5.5v7a.5.5 0 0 1-1 0v-7a.5.5 0 0 1 .5-.5zM8 5a.5.5 0 0 1 .5.5v7a.5.5 0 0 1-1 0v-7A.5.5 0 0 1 8 5zm3 .5v7a.5.5 0 0 1-1 0v-7a.5.5 0 0 1 1 0z" />
                    </svg>
                    <span>Clear All Animations</span>
                  </div>
                </div>
              );
            }
            return null;
          })()}
        </div>
      </>
    );
  }

  renderAnimationSelectionDialog() {
    const { animationSelectionDialog } = this.state;

    if (!animationSelectionDialog?.visible) return null;

    return (
      <>
        <div
          className="animation-dialog-overlay"
          onClick={this.hideAnimationSelectionDialog}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 999,
            background: "rgba(0, 0, 0, 0.3)",
          }}
        />
        <div
          className="animation-selection-dialog"
          style={{
            position: "fixed",
            left: animationSelectionDialog.x,
            top: animationSelectionDialog.y,
            zIndex: 1001,
            background: "#ffffff",
            borderRadius: "8px",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
            border: "1px solid #e5e7eb",
            minWidth: "280px",
            padding: "16px",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            style={{ marginBottom: "12px", fontWeight: 600, fontSize: "14px", color: "#374151" }}
          >
            Add Animation
          </div>

          {["box", "text"].map((category) => {
            const categoryAnimations = animationSelectionDialog.animationTypes.filter(
              (a) => a.category === category
            );

            return (
              <div key={category} style={{ marginBottom: "12px" }}>
                <div
                  style={{
                    fontSize: "12px",
                    fontWeight: 500,
                    color: "#6b7280",
                    marginBottom: "6px",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  {category} Animation
                </div>

                {categoryAnimations.map((anim) => (
                  <button
                    key={anim.id}
                    style={{
                      display: "block",
                      width: "100%",
                      padding: "8px 12px",
                      marginBottom: "4px",
                      border:
                        animationSelectionDialog.selectedType === anim.id
                          ? "2px solid #3b82f6"
                          : "1px solid #e5e7eb",
                      borderRadius: "4px",
                      background:
                        animationSelectionDialog.selectedType === anim.id ? "#eff6ff" : "#ffffff",
                      color: "#374151",
                      textAlign: "left",
                      cursor: "pointer",
                      fontSize: "13px",
                    }}
                    onClick={() => {
                      this.setState((prev) => ({
                        animationSelectionDialog: {
                          ...prev.animationSelectionDialog,
                          selectedType: anim.id,
                        },
                      }));
                    }}
                  >
                    {anim.label}
                  </button>
                ))}
              </div>
            );
          })}

          <div
            style={{
              display: "flex",
              gap: "8px",
              marginTop: "16px",
              paddingTop: "12px",
              borderTop: "1px solid #e5e7eb",
            }}
          >
            <button
              style={{
                flex: 1,
                padding: "8px 16px",
                border: "1px solid #e5e7eb",
                borderRadius: "4px",
                background: "#ffffff",
                color: "#374151",
                cursor: "pointer",
                fontSize: "13px",
              }}
              onClick={this.hideAnimationSelectionDialog}
            >
              Cancel
            </button>
            <button
              style={{
                flex: 1,
                padding: "8px 16px",
                border: "none",
                borderRadius: "4px",
                background: "#3b82f6",
                color: "#ffffff",
                cursor: "pointer",
                fontSize: "13px",
                fontWeight: 500,
              }}
              onClick={() =>
                this.handleAnimationTypeSelection(animationSelectionDialog.selectedType)
              }
            >
              Add
            </button>
          </div>
        </div>
      </>
    );
  }

  openTextAnimationModal() {
    this.setState({ showTextAnimationModal: true });
  }

  closeTextAnimationModal() {
    this.setState({
      showTextAnimationModal: false,
      currentEditingLayer: null,
    });
  }

  openBoxAnimationModal() {
    this.setState({ showBoxAnimationModal: true });
  }

  closeBoxAnimationModal() {
    this.setState({
      showBoxAnimationModal: false,
      currentEditingLayer: null,
    });
  }

  onApplyTextAnimation(animation, duration, delay = 0) {
    const { currentEditingLayer } = this.state;
    if (currentEditingLayer && this.props.onTextAnimationApplied) {
      this.props.onTextAnimationApplied(currentEditingLayer.box.id, animation, duration, delay);
    }
    this.closeTextAnimationModal();
  }

  onApplyBoxAnimation(animation, duration, delay = 0) {
    const { currentEditingLayer } = this.state;
    if (currentEditingLayer && this.props.onBoxAnimationApplied) {
      this.props.onBoxAnimationApplied(currentEditingLayer.box.id, animation, duration, delay);
    }
    this.closeBoxAnimationModal();
  }

  onDeleteTextAnimation() {
    const { currentEditingLayer } = this.state;
    if (currentEditingLayer && this.props.onTextAnimationDeleted) {
      this.props.onTextAnimationDeleted(currentEditingLayer.box.id);
    }
    this.closeTextAnimationModal();
  }

  onDeleteBoxAnimation() {
    const { currentEditingLayer } = this.state;
    if (currentEditingLayer && this.props.onBoxAnimationDeleted) {
      this.props.onBoxAnimationDeleted(currentEditingLayer.box.id);
    }
    this.closeBoxAnimationModal();
  }

  render() {
    const { visible = true } = this.props;

    if (!visible) return null;

    return (
      <div className="timeline-panel">
        <div className="timeline-body">
          {this.state.showLayerPanel && this.renderLayerPanel()}
          <div className="timeline-main" ref={this.timelineRef}>
            {this.renderTimeRuler()}
            {this.renderTimelineTracks()}
          </div>
        </div>
        {this.renderTimelineFooter()}
        {this.renderContextMenu()}
        {this.renderAnimationSelectionDialog()}

        <TextAnimationModal
          visible={this.state.showTextAnimationModal}
          onClose={this.closeTextAnimationModal}
          onApply={this.onApplyTextAnimation}
          onDelete={
            this.state.currentEditingLayer?.box?.textAnimation ? this.onDeleteTextAnimation : null
          }
          initialAnimation={this.state.currentEditingLayer?.box?.textAnimation?.animation}
          initialDuration={this.state.currentEditingLayer?.box?.textAnimation?.duration}
          initialDelay={this.state.currentEditingLayer?.box?.textAnimation?.delay}
        />

        <BoxAnimationModal
          visible={this.state.showBoxAnimationModal}
          onClose={this.closeBoxAnimationModal}
          onApply={this.onApplyBoxAnimation}
          onDelete={
            this.state.currentEditingLayer?.box?.boxAnimation ? this.onDeleteBoxAnimation : null
          }
          initialAnimation={this.state.currentEditingLayer?.box?.boxAnimation?.animation}
          initialDuration={this.state.currentEditingLayer?.box?.boxAnimation?.duration}
          initialDelay={this.state.currentEditingLayer?.box?.boxAnimation?.delay}
        />
      </div>
    );
  }
}

RemotionTimeline.propTypes = {
  boxes: PropTypes.object,
  animations: PropTypes.object,
  currentScene: PropTypes.string,
  navItemsById: PropTypes.object,
  pluginToolbars: PropTypes.object,
  soundList: PropTypes.object,
  visible: PropTypes.bool,
  showPlayer: PropTypes.bool,
  useConsistentColors: PropTypes.bool,
  defaultLayerColor: PropTypes.string,
  onColorModeToggle: PropTypes.func,
  onLayerSelect: PropTypes.func,
  onLayerReorder: PropTypes.func,
  onLayerVisibilityToggle: PropTypes.func,
  onUpdateBoxVisibility: PropTypes.func,
  onTimingChange: PropTypes.func,
  onAnimationDurationChange: PropTypes.func,
  onAddLayer: PropTypes.func,
  onDeleteLayer: PropTypes.func,
  onDuplicateLayer: PropTypes.func,
  onPlay: PropTypes.func,
  onPause: PropTypes.func,
  onPauseAnimations: PropTypes.func,
  onSeek: PropTypes.func,
  onTriggerAnimation: PropTypes.func,
  onTriggerLayerAnimation: PropTypes.func,
  onAnimationReset: PropTypes.func,
  onTimeUpdate: PropTypes.func,
  onLayerTimingChange: PropTypes.func,
  onAnimationAdd: PropTypes.func,
  onAnimationUpdate: PropTypes.func,
  onTextAnimationUpdate: PropTypes.func,
  onBoxAnimationUpdate: PropTypes.func,
  onAnimationSync: PropTypes.func,
  onSceneDurationChange: PropTypes.func,
  onSaveNewAnimation: PropTypes.func,
  editAnimation: PropTypes.func,
  playAnimation: PropTypes.func,
  pauseAnimation: PropTypes.func,
  setCurrentTime: PropTypes.func,
  onTextAnimationApplied: PropTypes.func,
  onBoxAnimationApplied: PropTypes.func,
  onTextAnimationDeleted: PropTypes.func,
  onBoxAnimationDeleted: PropTypes.func,
};

export default RemotionTimeline;
